import { EQpHttpStatusCode } from '@library/models/qp-http.models';
import { QpAlertErrorService } from '@library/services/qp-alert-error/qp-alert-error.service';
import { QpLoggerService } from '@library/services/qp-logger/qp-logger.service';
import { QpNotificationBarService } from '@library/services/qp-notification-bar/qp-notification-bar.service';
import { CommonRoutes } from '@one/app/common-routes';
import { INTERCEPTOR_SKIP_HEADER } from '@one/app/shared/interceptors/inspection.interceptor';
import { HttpInterceptor, HttpRequest, HttpErrorResponse, HttpHandler, HttpEvent } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

/** Dummy base used to resolve relative request URLs (e.g. `api/inspections/1`) to inspect their `pathname`. */
const URL_RESOLUTION_BASE = 'https://qima-error-handler.invalid/';
const AUTHENTICATE_SERVICE_ERRORS = [EQpHttpStatusCode.GATEWAY_TIMEOUT, EQpHttpStatusCode.NOT_FOUND, EQpHttpStatusCode.SERVICE_UNAVAILABLE];
/** Late AQL/sampling PUTs after COMPLETED return 403 OPERATION_NOT_PERMITTED — not a session permission loss. */
const INSPECTION_SAMPLING_SIZE_MUTATION_PATH =
  /^\/api\/inspections\/(?:\d+|[a-f\d]{8}-(?:[a-f\d]{4}-){3}[a-f\d]{12})\/(?:aql-)?sampling-sizes$/;
/** Optional CC prefill on approve/reject modal — failure must not lock the user out of the report flow. */
const REPORT_DECISION_LAST_CC_RECIPIENTS_PATH = /^\/api\/report-decisions\/last-cc-recipients$/;

/**
 * @description
 * Whether the request targets our backend, based on its resolved `pathname` (`/api/...`) rather than a
 * plain substring match, so paths like `ngx-qima/assets/api/icons/foo.svg` are correctly excluded.
 * @param {string} url the request URL, relative or absolute
 * @returns {boolean} true if this is a backend `/api/` call
 */
function isApiRequest(url: string): boolean {
  return new URL(url, URL_RESOLUTION_BASE).pathname.startsWith('/api/');
}

/**
 * @description
 * Sampling-size mutations that can 403 once the inspection is no longer editable for the inspector.
 * @param {HttpRequest<unknown>} request the failed request
 * @returns {boolean} true for PUT/PATCH `.../aql-sampling-sizes` or `.../sampling-sizes`
 */
function isInspectionSamplingSizeMutationRequest(request: HttpRequest<unknown>): boolean {
  if (request.method !== 'PUT' && request.method !== 'PATCH') {
    return false;
  }

  return INSPECTION_SAMPLING_SIZE_MUTATION_PATH.test(new URL(request.url, URL_RESOLUTION_BASE).pathname);
}

/**
 * @description
 * Best-effort CC recipient prefill when opening the report approve/reject modal.
 * @param {HttpRequest<unknown>} request the failed request
 * @returns {boolean} true for GET `/api/report-decisions/last-cc-recipients`
 */
function isReportDecisionLastCcRecipientsRequest(request: HttpRequest<unknown>): boolean {
  if (request.method !== 'GET') {
    return false;
  }

  return REPORT_DECISION_LAST_CC_RECIPIENTS_PATH.test(new URL(request.url, URL_RESOLUTION_BASE).pathname);
}

/**
 * @description
 * Not provided in root on purpose
 */
@Injectable()
export class ErrorHandlerInterceptor implements HttpInterceptor {
  public readonly _qpAlertErrorService = inject(QpAlertErrorService);
  public readonly _router = inject(Router);
  public readonly _qpLoggerService = inject(QpLoggerService);
  public readonly _qpNotificationBarService = inject(QpNotificationBarService);

  public intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      tap({
        error: (error: HttpErrorResponse | Error | unknown): void | never => {
          const shouldSkipInterceptor = request.headers.has(INTERCEPTOR_SKIP_HEADER);

          // Only handle backend API failures. Static asset / icon HTTP loads (e.g. ngx-qima SVGs via
          // angular-svg-icon) must not toast or navigate when they fail offline.
          if (!isApiRequest(request.url)) {
            this._qpLoggerService.debug('Ignored non-API HTTP error', error);

            return;
          }

          if (!shouldSkipInterceptor) {
            this._qpLoggerService.debug('Caught HTTP error');
            this._qpLoggerService.error(error);
          }

          if (error instanceof HttpErrorResponse) {
            if (AUTHENTICATE_SERVICE_ERRORS.includes(error.status) && error.url?.includes('/api/authenticate')) {
              void this._router.navigate([CommonRoutes.errorUnknown()]);

              return;
            }

            // Sync/outbox requests set X-Skip-Interceptor; skip user toasts (incl. image 409 "already exists").
            if (!(error.status === EQpHttpStatusCode.UNAUTHORIZED && (error.message === '' || error.url?.includes('/api/account')))) {
              if (!shouldSkipInterceptor) {
                this._qpAlertErrorService.handleError(error);
              }
            }

            if (error.status === EQpHttpStatusCode.FORBIDDEN && error.url?.includes('/api/users')) {
              this._qpNotificationBarService.error('one-brd-team-inspector-consult.error.assigned-inspection');

              return;
            }

            // Background outbox replay already sets X-Skip-Interceptor. Do not kick the active user
            // to /forbidden when a stale queued action returns 403 under a different account/session.
            // Also keep the inspector on the success flow when a late sampling-size sync gets
            // OPERATION_NOT_PERMITTED after the inspection is already COMPLETED (toast via handleError).
            if (error.status === EQpHttpStatusCode.FORBIDDEN && request.method !== 'HEAD' && !shouldSkipInterceptor) {
              if (isInspectionSamplingSizeMutationRequest(request)) {
                return;
              }

              if (isReportDecisionLastCcRecipientsRequest(request)) {
                return;
              }

              void this._router.navigate([CommonRoutes.forbidden()]);

              return;
            }

            // Never wrap HttpErrorResponse. The 3.29.0 lint wrap stripped .status so the
            // outbox FIFO could not classify failures (500, 400, …). Later allowlists
            // (401, 403, 409, 0, 502, 503, 504) never covered 500. AuthExpired,
            // pause/probe, 409-as-success, and #11414 403/409 purge all need the original.
            return;
          } else if (error instanceof Error) {
            // Rethrow since not handled
            throw error;
          }

          // Rethrow since not handled
          throw new Error('Unknown error thrown');
        },
      })
    );
  }
}
